// "Report a language issue": what a learner can report and how a report moves through review. Pure data, shared by
// the report dialog, the API route (/api/reports) and the reviewers' inbox (/admin/reports).

import type { Course } from '@/lib/plans'

export type ReportKind = 'form' | 'meaning' | 'translation' | 'pronunciation' | 'explanation' | 'other'

export type ReportStatus = 'new' | 'confirmed' | 'fixed' | 'dismissed'

export interface Bilingual {
    hi: string
    en: string
}

export const REPORT_KINDS: { id: ReportKind, label: Bilingual }[] = [
    { id: 'form', label: { hi: 'रूप या वर्तनी ग़लत है', en: 'A form or spelling is wrong' } },
    { id: 'meaning', label: { hi: 'अर्थ ग़लत है', en: 'A meaning is wrong' } },
    { id: 'translation', label: { hi: 'अनुवाद ठीक नहीं है', en: 'A translation is not right' } },
    { id: 'pronunciation', label: { hi: 'उच्चारण ठीक नहीं है', en: 'The pronunciation is not right' } },
    { id: 'explanation', label: { hi: 'समझाना अस्पष्ट या ग़लत है', en: 'An explanation is unclear or wrong' } },
    { id: 'other', label: { hi: 'कुछ और', en: 'Something else' } }
]

export const REPORT_STATUSES: { id: ReportStatus, label: Bilingual }[] = [
    { id: 'new', label: { hi: 'नया', en: 'New' } },
    { id: 'confirmed', label: { hi: 'सही पाया, ठीक करना है', en: 'Confirmed, to fix' } },
    { id: 'fixed', label: { hi: 'ठीक किया', en: 'Fixed' } },
    { id: 'dismissed', label: { hi: 'बदलाव नहीं चाहिए', en: 'No change needed' } }
]

export const REPORT_LIMITS = {
    messageMin: 5,
    messageMax: 2000,
    excerpt: 300,
    page: 300,
    itemKey: 300,
    resolution: 1000
}

export const isReportKind = (value: unknown): value is ReportKind => REPORT_KINDS.some(k => k.id === value)

export const isReportStatus = (value: unknown): value is ReportStatus => REPORT_STATUSES.some(s => s.id === value)

export const reportKindLabel = (kind: ReportKind): Bilingual => REPORT_KINDS.find(k => k.id === kind)?.label ?? { hi: kind, en: kind }

export const reportStatusLabel = (status: ReportStatus): Bilingual => REPORT_STATUSES.find(s => s.id === status)?.label ?? { hi: status, en: status }

/** Which course a page belongs to, from its path (everything under /sanskrit is the Sanskrit course). */
export const courseOfPath = (path: string): Course => (path === '/sanskrit' || path.startsWith('/sanskrit/') ? 'sanskrit' : 'prakrit')

/** What the report dialog sends. */
export interface ReportRequest {
    course: Course
    page: string
    itemKey?: string
    excerpt?: string
    kind: ReportKind
    message: string
    replyEmail?: string
    website?: string
    idempotencyKey: string
}
